import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import usersApi from '../api/users';
import { toast } from 'react-toastify';

// Hook to fetch all users
export const useUsers = (params = {}) => {
  return useQuery({
    queryKey: ['users', params],
    queryFn: () => usersApi.list(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook to fetch a single user
export const useUser = (userId) => {
  return useQuery({
    queryKey: ['user', userId],
    queryFn: () => usersApi.get(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

// Hook to create a user
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userData) => usersApi.create(userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User created successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    },
  });
};

// Hook to update a user
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, userData }) => usersApi.update(userId, userData),
    onSuccess: (data, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
      toast.success('User updated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    },
  });
};

// Hook to delete a user
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId) => usersApi.delete(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    },
  });
};

// Hook to update user permissions
export const useUpdateUserPermissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, permissions }) => usersApi.updatePermissions(userId, permissions),
    onSuccess: (data, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
      toast.success('Permissions updated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    },
  });
};

// Hook to update user schedule
export const useUpdateUserSchedule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, schedule }) => usersApi.updateSchedule(userId, schedule),
    onSuccess: (data, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
      toast.success('Schedule updated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update schedule');
    },
  });
};

// Hook to update user status
export const useUpdateUserStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, status }) => usersApi.updateStatus(userId, status),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(`User ${variables.status === 'active' ? 'activated' : 'deactivated'} successfully`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    },
  });
};

// Hook for bulk user updates
export const useBulkUpdateUsers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userIds, updates }) => usersApi.bulkUpdate(userIds, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Users updated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update users');
    },
  });
};

// Hook to fetch user activity
export const useUserActivity = (userId, params = {}) => {
  return useQuery({
    queryKey: ['user-activity', userId, params],
    queryFn: () => usersApi.getActivity(userId, params),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook to fetch permissions matrix
export const usePermissionsMatrix = () => {
  return useQuery({
    queryKey: ['permissions-matrix'],
    queryFn: () => usersApi.getPermissionsMatrix(),
    staleTime: 30 * 60 * 1000, // 30 minutes
    cacheTime: 60 * 60 * 1000, // 1 hour
  });
};

// Hook to fetch user roles
export const useUserRoles = () => {
  return useQuery({
    queryKey: ['user-roles'],
    queryFn: () => usersApi.getRoles(),
    staleTime: 30 * 60 * 1000, // 30 minutes
    cacheTime: 60 * 60 * 1000, // 1 hour
  });
};